import type {
  AgentOption,
  ClientOption,
  ClientPurchaseOrder,
  FieldEngineerRecommendation,
  ParentTicketFormValues,
  ParentTicketAttachmentRow,
  ParentTicketAttachmentSource,
  RequestOption,
  TeamOption,
  ToolPartCatalogOption,
  ToolPartSearchResponse,
} from '@/types/ticket'
import type {
  FESchedule,
  ParentTicketStatus,
  SeverityLabel,
  TicketPart,
  TicketUpdate,
  TicketUpdateFormValues,
} from '@/types/parentTicket'
import axios from 'axios'
import { useParentTicketStore } from '@/store/parentTicket'
import { apiClient } from '@/lib/api/client'
import { buildParentTicketApiBody } from '@/lib/utils/parentTicketFormPayload'
import { buildAttachmentUploadFormData, mapApiAttachments } from '@/lib/utils/parentTicketAttachments'
import { formatApiErrorMessage } from '@/lib/utils/errorHandler'

/** Backend FE recommend endpoints accept at most 50 candidates. */
const MAX_FE_RECOMMEND_LIMIT = 50

function clampFeRecommendLimit(limit?: number): number {
  const requested = limit ?? 10
  return Math.min(Math.max(1, requested), MAX_FE_RECOMMEND_LIMIT)
}

export type FERecommendationOptions = {
  schedule?: string | null
  limit?: number
  requiredCertifications?: string[]
  areaClusterId?: number | null
  region?: string | null
  province?: string | null
}

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
  errors?: Record<string, string[]>
  code?: string
  linkedParts?: Array<{ id: number; part_name: string; child_ticket_id?: number | null }>
  status?: number
  brandResolveFailed?: boolean
}

/**
 * Normalize ticket/request API payloads (camelCase or snake_case) into RequestOption.
 * Ensures siteAddress is available for FE dispatch Nominatim seeding on edit.
 */
export function mapApiRequestToRequestOption(raw: Record<string, unknown>): RequestOption {
  const categories: string[] = []
  if (typeof raw.category === 'string' && raw.category) {
    categories.push(raw.category)
  } else if (Array.isArray(raw.categories)) {
    for (const item of raw.categories) {
      if (typeof item === 'string' && item) categories.push(item)
    }
  }

  const siteAddress =
    (typeof raw.siteAddress === 'string' && raw.siteAddress) ||
    (typeof raw.site_address === 'string' && raw.site_address) ||
    (typeof raw.location === 'string' && raw.location) ||
    undefined

  const explicitDbId = raw.requestDbId ?? raw.request_db_id
  let requestDbId: number | undefined
  if (typeof explicitDbId === 'number' && Number.isFinite(explicitDbId)) {
    requestDbId = explicitDbId
  } else if (typeof explicitDbId === 'string' && /^\d+$/.test(explicitDbId)) {
    requestDbId = Number(explicitDbId)
  } else if (raw.request_number != null && raw.id != null && String(raw.id) !== String(raw.request_number)) {
    // TicketRequestResource: numeric PK in `id`, display number in `request_number`
    const idNum = Number(raw.id)
    if (Number.isFinite(idNum)) requestDbId = idNum
  } else if (raw.request_number == null && typeof raw.id === 'number' && Number.isFinite(raw.id)) {
    requestDbId = raw.id
  }
  // When nested parent-ticket `request.id` is the request_number, leave requestDbId unset
  // unless requestDbId was provided explicitly.

  const categoryIdsRaw = raw.category_ids ?? raw.categoryIds
  const categoryIds = Array.isArray(categoryIdsRaw)
    ? categoryIdsRaw.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
    : undefined

  return {
    id: String(raw.request_number ?? raw.id ?? ''),
    requestDbId,
    clientName: String(raw.clientName ?? raw.contact_name ?? raw.client_name ?? ''),
    clientOrg:
      (typeof raw.clientOrg === 'string' && raw.clientOrg) ||
      (typeof raw.client_name === 'string' && raw.client_name) ||
      (raw.client && typeof raw.client === 'object' && typeof (raw.client as { name?: string }).name === 'string'
        ? (raw.client as { name: string }).name
        : undefined) ||
      undefined,
    serialNumber: (raw.serialNumber ?? raw.serial_number) as string | undefined,
    unitModel: (raw.unitModel ?? raw.unit_model) as string | undefined,
    requestedAt: String(raw.requestedAt ?? raw.requested_at ?? raw.created_at ?? raw.submittedAt ?? new Date().toISOString()),
    issueDescription: (raw.issueDescription ?? raw.description) as string | undefined,
    categories: categories.length > 0 ? categories : undefined,
    categoryId: (raw.categoryId ?? raw.category_id) as number | undefined,
    categoryIds: categoryIds && categoryIds.length > 0 ? categoryIds : undefined,
    description: (raw.description ?? raw.issueDescription) as string | undefined,
    requestedQuantity: (raw.requestedQuantity ?? raw.requested_quantity) as number | undefined,
    preferredScheduleAt: (raw.preferredScheduleAt ?? raw.preferred_schedule_at) as string | undefined,
    preferredScheduleUntil: (raw.preferredScheduleUntil ?? raw.preferred_schedule_until) as string | undefined,
    siteAddress: siteAddress || undefined,
    contactAvailabilityText: (raw.contactAvailabilityText ?? raw.contact_availability_text) as string | undefined,
    clientLabel: (raw.clientLabel ?? raw.client_label) as string | undefined,
    attachmentMetadata: (raw.attachment_metadata ?? raw.attachmentMetadata) as RequestOption['attachmentMetadata'],
    contactNumber: (raw.contactNumber ?? raw.contact_number) as string | undefined,
    contactName: (raw.contactName ?? raw.contact_name) as string | undefined,
    contactEmail: (raw.contactEmail ?? raw.contact_email) as string | undefined,
    preferredContactAt: (raw.preferredContactAt ?? raw.preferred_contact_at) as string | undefined,
    serviceTypeId: (raw.serviceTypeId ?? raw.service_type_id) as number | undefined,
    serviceType: (raw.serviceType ?? raw.service_type) as string | undefined,
    serviceParent: (raw.serviceParent ?? raw.service_parent) as string | undefined,
    ticketStatus: (raw.ticketStatus ?? raw.ticket_status) as number | undefined,
    ticketStatusLabel: (raw.ticketStatusLabel ?? raw.ticket_status_label) as string | undefined,
    priority: raw.priority as string | undefined,
    requestSource: (raw.requestSource ?? raw.request_source) as string | undefined,
    requestChannel: (raw.requestChannel ?? raw.request_channel) as string | undefined,
    submittedAt: (raw.submittedAt ?? raw.submitted_at) as string | undefined,
    clientId: (raw.clientId ?? raw.client_id) as number | undefined,
    clientCode: (raw.clientCode ?? raw.client_code) as string | undefined,
    assignedTo: (raw.assignedTo ?? raw.assigned_to) as number | undefined,
    assignedToName: (raw.assignedToName ?? raw.assigned_to_name) as string | undefined,
    assignedTeamId: (raw.assignedTeamId ?? raw.assigned_team_id) as number | undefined,
  }
}

export const TicketService = {
  async getRequestOptions(): Promise<ApiResponse<RequestOption[]>> {
    return { success: true, data: [] }
  },

  async searchRequestOptions(searchTerm: string): Promise<ApiResponse<RequestOption[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const response = await apiClient.get('/support/my-request', {
        params: {
          search: searchTerm,
          incoming: 1, // Filter for submitted requests (submitted_at IS NOT NULL)
          acknowledged: 1, // Filter for acknowledged requests (acknowledged_at IS NOT NULL)
          per_page: 20,
        },
      })

      const requestsData = response.data?.data || []
      
      // Transform API response to RequestOption format (siteAddress from camel or snake case)
      const transformedRequests: RequestOption[] = requestsData.map((req: Record<string, unknown>) => {
        const mapped = mapApiRequestToRequestOption(req)
        return {
          ...mapped,
          attachmentMetadata: (req.attachment_metadata ?? req.attachmentMetadata) as RequestOption['attachmentMetadata'],
          acknowledgedAt: (req.acknowledged_at ?? req.acknowledgedAt) as string | undefined,
          acknowledgedBy: (req.acknowledged_by ?? req.acknowledgedBy) as number | undefined,
          resolvedAt: (req.resolved_at ?? req.resolvedAt) as string | undefined,
          closedAt: (req.closed_at ?? req.closedAt) as string | undefined,
          createdAt: req.created_at || undefined,
          updatedAt: req.updated_at || undefined,
          // Assignment
          assignedTo: req.assigned_to || undefined,
          assignedTeamId: req.assigned_team_id || undefined,
          // External References
          externalReferenceId: req.external_reference_id || undefined,
          externalThreadId: req.external_thread_id || undefined,
          // User/Requestor
          userId: req.user_id || undefined,
          user: req.user || undefined,
          // Ticket ID
          ticketId: req.ticket_id || undefined,
          // Client ID (to determine which database to search stores from)
          clientId: req.client_id || undefined,
          // Client Code (to set X-Client-Code header for store search)
          clientCode: req.client_code || undefined,
        }
      })

      return { success: true, data: transformedRequests }
    } catch (error: any) {
      console.error('Error searching request options:', error)
      return { 
        success: false, 
        message: error?.response?.data?.message || error?.message || 'Failed to search requests',
        data: [] 
      }
    }
  },

  async getClientOptions(): Promise<ApiResponse<ClientOption[]>> {
    return { success: true, data: [] }
  },

  async searchTeamOptions(searchTerm: string): Promise<ApiResponse<TeamOption[]>> {
    try {
      const response = await apiClient.get('/options/teams')
      const data = response.data?.data ?? response.data ?? []
      const list = Array.isArray(data) ? data : []
      const teams: TeamOption[] = list.map((row: Record<string, unknown>) => ({
        id: Number(row.id),
        name: String(row.name ?? row.label ?? ''),
        description: row.description != null ? String(row.description) : null,
      }))
      const term = searchTerm.trim().toLowerCase()
      const filtered = term
        ? teams.filter((team) => team.name.toLowerCase().includes(term))
        : teams
      return { success: true, data: filtered }
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to search teams',
        data: [],
      }
    }
  },

  async searchHelpdeskAgentOptions(searchTerm: string): Promise<ApiResponse<AgentOption[]>> {
    try {
      if (searchTerm.length < 2) {
        return { success: true, data: [] }
      }
      const response = await apiClient.get('/options/helpdesk-users/search', {
        params: { search: searchTerm },
      })
      const data = response.data?.data ?? response.data ?? []
      const list = Array.isArray(data) ? data : []
      const agents: AgentOption[] = list.map((u: any) => ({
        id: u.id,
        user_login: u.user_login,
        user_email: u.user_email,
        first_name: u.first_name,
        last_name: u.last_name,
        employee_id: u.employee_id,
      }))
      return { success: true, data: agents }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to search agents',
        data: [],
      }
    }
  },

  _mapFieldEngineerRecommendations(raw: unknown[]): FieldEngineerRecommendation[] {
    return raw.map((item) => {
      const rec = item as Record<string, unknown>
      return {
        field_engineer_id: Number(rec.field_engineer_id ?? rec.id ?? 0),
        user_id: rec.user_id != null ? Number(rec.user_id) : null,
        full_name: String(rec.full_name ?? ''),
        cluster: rec.cluster != null ? String(rec.cluster) : undefined,
        launch_point: rec.launch_point != null ? String(rec.launch_point) : undefined,
        distance_km: rec.distance_km != null ? Number(rec.distance_km) : null,
        distance_source: rec.distance_source != null ? String(rec.distance_source) : null,
        score: Number(rec.score ?? 0),
        match_reasons: Array.isArray(rec.match_reasons) ? (rec.match_reasons as string[]) : [],
        certifications: Array.isArray(rec.certifications)
          ? (rec.certifications as FieldEngineerRecommendation['certifications'])
          : [],
        has_required_certifications:
          rec.has_required_certifications != null
            ? Boolean(rec.has_required_certifications)
            : undefined,
        workload: rec.workload != null ? Number(rec.workload) : undefined,
        daily_capacity: rec.daily_capacity != null ? Number(rec.daily_capacity) : undefined,
        apt_travel_minutes: rec.apt_travel_minutes != null ? Number(rec.apt_travel_minutes) : null,
        apt_repair_minutes: rec.apt_repair_minutes != null ? Number(rec.apt_repair_minutes) : null,
        schedule_label: rec.schedule_label != null ? String(rec.schedule_label) : null,
        available_at_schedule:
          rec.available_at_schedule != null ? Boolean(rec.available_at_schedule) : null,
        is_locked: Boolean(rec.is_locked),
        lock_expires_at: rec.lock_expires_at != null ? String(rec.lock_expires_at) : null,
        lock_seconds_remaining:
          rec.lock_seconds_remaining != null ? Number(rec.lock_seconds_remaining) : null,
        locked_by_you: Boolean(rec.locked_by_you),
        latitude: rec.latitude != null && Number.isFinite(Number(rec.latitude)) ? Number(rec.latitude) : null,
        longitude:
          rec.longitude != null && Number.isFinite(Number(rec.longitude)) ? Number(rec.longitude) : null,
      }
    })
  },

  /** Store-scoped recommendations (create flow before ticket exists). */
  async recommendFieldEngineers(
    storeId: number,
    clientCode?: string,
    options?: FERecommendationOptions,
  ): Promise<ApiResponse<FieldEngineerRecommendation[]>> {
    try {
      const headers: Record<string, string> = {}
      if (clientCode) headers['X-Client-Code'] = clientCode
      const response = await apiClient.get(`/organization/field-engineers/recommend/${storeId}`, {
        params: {
          limit: clampFeRecommendLimit(options?.limit),
          ...(options?.schedule ? { schedule: options.schedule } : {}),
          ...(options?.requiredCertifications?.length
            ? { required_certifications: options.requiredCertifications }
            : {}),
          ...(options?.areaClusterId ? { area_cluster_id: options.areaClusterId } : {}),
          ...(options?.region ? { region: options.region } : {}),
          ...(options?.province ? { province: options.province } : {}),
        },
        headers,
      })
      const recommendationsData = response.data?.data?.recommendations || []
      const recommendations = TicketService._mapFieldEngineerRecommendations(
        Array.isArray(recommendationsData) ? recommendationsData : [],
      )
      return { success: true, data: recommendations }
    } catch (error: any) {
      return {
        success: false,
        message: formatApiErrorMessage(
          error?.response?.data,
          error?.message || 'Unable to load field engineer recommendations. Please try again.',
        ),
        data: [],
      }
    }
  },

  /** Coordinate-scoped recommendations (free-text location flow before ticket exists). */
  async recommendFieldEngineersByLocation(
    latitude: number,
    longitude: number,
    clientCode?: string,
    options?: FERecommendationOptions,
  ): Promise<ApiResponse<FieldEngineerRecommendation[]>> {
    try {
      const headers: Record<string, string> = {}
      if (clientCode) headers['X-Client-Code'] = clientCode
      const response = await apiClient.get('/organization/field-engineers/recommend/location', {
        params: {
          latitude,
          longitude,
          limit: clampFeRecommendLimit(options?.limit),
          ...(options?.schedule ? { schedule: options.schedule } : {}),
          ...(options?.requiredCertifications?.length
            ? { required_certifications: options.requiredCertifications }
            : {}),
          ...(options?.areaClusterId ? { area_cluster_id: options.areaClusterId } : {}),
          ...(options?.region ? { region: options.region } : {}),
          ...(options?.province ? { province: options.province } : {}),
        },
        headers,
      })
      const recommendationsData = response.data?.data?.recommendations || response.data?.data || []
      const recommendations = TicketService._mapFieldEngineerRecommendations(
        Array.isArray(recommendationsData) ? recommendationsData : [],
      )
      return { success: true, data: recommendations }
    } catch (error: any) {
      return {
        success: false,
        message: formatApiErrorMessage(
          error?.response?.data,
          error?.message || 'Unable to load field engineer recommendations. Please try again.',
        ),
        data: [],
      }
    }
  },

  /** Check primary/additional engineer availability for a proposed schedule. */
  async checkEngineerConflicts(
    ticketId: string,
    payload: {
      primary_engineer_id?: number | null
      additional_fe_user_id?: number | null
      /** @deprecated Prefer additional_fe_user_id */
      backup_engineer_id?: number | null
      schedule?: string | null
    },
  ): Promise<
    ApiResponse<{
      primary: { available: boolean; conflict_reason?: string }
      additional?: { available: boolean; conflict_reason?: string }
      /** @deprecated Prefer additional */
      backup: { available: boolean; conflict_reason?: string }
    }>
  > {
    try {
      const additionalId = payload.additional_fe_user_id ?? payload.backup_engineer_id
      const response = await apiClient.post(`/tickets/${ticketId}/check-engineer-conflicts`, {
        ...payload,
        additional_fe_user_id: additionalId ?? undefined,
        backup_engineer_id: additionalId ?? undefined,
      })
      const data = response.data?.data ?? response.data
      if (data && data.additional == null && data.backup != null) {
        data.additional = data.backup
      }
      if (data && data.backup == null && data.additional != null) {
        data.backup = data.additional
      }
      return { success: true, data }
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message || error?.message || 'Failed to check engineer conflicts',
      }
    }
  },

  /** Ticket-scoped recommendations (uses ticket store + excludes assigned engineers). */
  async recommendEngineersForTicket(
    ticketId: string,
    options?: FERecommendationOptions,
  ): Promise<ApiResponse<FieldEngineerRecommendation[]>> {
    try {
      const params: Record<string, string | number | string[]> = {}
      if (options?.schedule) params.schedule = options.schedule
      if (options?.limit) params.limit = clampFeRecommendLimit(options.limit)
      if (options?.requiredCertifications?.length) {
        params.required_certifications = options.requiredCertifications
      }
      if (options?.areaClusterId) params.area_cluster_id = options.areaClusterId
      if (options?.region) params.region = options.region
      if (options?.province) params.province = options.province
      const response = await apiClient.get(`/tickets/${ticketId}/recommend-engineers`, { params })
      const data = response.data?.data ?? []
      const recommendations = TicketService._mapFieldEngineerRecommendations(
        Array.isArray(data) ? data : [],
      )
      return {
        success: true,
        data: recommendations,
        brandResolveFailed: Boolean(response.data?.brand_resolve_failed),
      }
    } catch (error: any) {
      return {
        success: false,
        message: formatApiErrorMessage(
          error?.response?.data,
          error?.message || 'Unable to load field engineer recommendations. Please try again.',
        ),
        data: [],
      }
    }
  },

  /**
   * Phase 6: Check authoritative eligibility for assigned engineers independent of
   * recommendation ranking limits. Returns per-engineer eligible/ineligible with reason codes.
   * Falls back gracefully when the backend endpoint is not yet available.
   */
  async checkEngineerEligibility(
    ticketId: string,
    feUserIds: number[],
    options?: {
      clientId?: number
      schedule?: string
      schedule_date?: string
      schedule_time?: string
      schedule_end_date?: string
      schedule_end_time?: string
      latitude?: number | null
      longitude?: number | null
      location_address?: string | null
      requiredCertifications?: string[]
      exclude_schedule_id?: number | null
      role?: 'primary' | 'additional' | 'secondary'
    },
  ): Promise<ApiResponse<Record<number, {
    fe_user_id: number
    eligible: boolean
    reasons: string[]
    reason_codes: string[]
    unknown?: boolean
  }>>> {
    if (!feUserIds.length) return { success: true, data: {} }
    try {
      const payload: Record<string, unknown> = { fe_user_ids: feUserIds }
      if (options?.clientId != null) payload.clientId = options.clientId
      if (options?.schedule_date) payload.schedule_date = options.schedule_date
      if (options?.schedule) payload.schedule = options.schedule
      if (options?.schedule_time) payload.schedule_time = options.schedule_time
      if (options?.schedule_end_date) payload.schedule_end_date = options.schedule_end_date
      if (options?.schedule_end_time) payload.schedule_end_time = options.schedule_end_time
      if (options?.latitude != null) payload.latitude = options.latitude
      if (options?.longitude != null) payload.longitude = options.longitude
      if (options?.location_address) payload.location_address = options.location_address
      if (options?.requiredCertifications !== undefined) {
        payload.required_certifications = options.requiredCertifications
      }
      if (options?.exclude_schedule_id != null) {
        payload.exclude_schedule_id = options.exclude_schedule_id
      }
      if (options?.role) payload.role = options.role
      const response = await apiClient.post(`/tickets/${ticketId}/evaluate-engineer-eligibility`, payload)
      const raw = response.data?.data
      const list = Array.isArray(raw) ? raw : []
      const mapped: Record<number, {
        fe_user_id: number
        eligible: boolean
        reasons: string[]
        reason_codes: string[]
      }> = {}
      for (const entry of list) {
        const id = Number(entry?.fe_user_id)
        if (!Number.isFinite(id) || id <= 0) continue
        const reasonCodes = Array.isArray(entry?.reason_codes) ? entry.reason_codes.map(String) : []
        mapped[id] = {
          fe_user_id: id,
          eligible: Boolean(entry?.eligible),
          reason_codes: reasonCodes,
          reasons: reasonCodes.map((code: string) => code.replace(/_/g, ' ')),
        }
      }
      return { success: true, data: mapped }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        return { success: false, message: 'eligibility_endpoint_unavailable', data: {} }
      }
      return {
        success: false,
        message: err?.response?.data?.message ?? err?.message ?? 'Failed to check engineer eligibility',
        data: {},
      }
    }
  },

  /** Search all field engineers by name/cluster (manual dispatch pick list). */
  async searchFieldEngineers(
    query: string,
    options?: { perPage?: number; allowEmpty?: boolean },
  ): Promise<ApiResponse<FieldEngineerRecommendation[]>> {
    const trimmed = query.trim()
    if (trimmed.length < 2 && !options?.allowEmpty) {
      return { success: true, data: [] }
    }
    try {
      const response = await apiClient.get('/organization/field-engineers', {
        params: {
          ...(trimmed ? { search: trimmed } : {}),
          per_page: options?.perPage ?? 50,
          active: 1,
        },
      })
      const rows = response.data?.data ?? response.data ?? []
      const list = Array.isArray(rows) ? rows : []
      const recommendations = TicketService._mapFieldEngineerRecommendations(
        list.map((item) => {
          const rec = item as Record<string, unknown>
          return {
            id: rec.id,
            user_id: rec.user_id,
            full_name: rec.full_name,
            cluster: rec.cluster,
            launch_point: rec.launch_point,
            province: rec.province,
            score: 0,
          }
        }),
      )
      return { success: true, data: recommendations }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: err?.response?.data?.message || err?.message || 'Failed to search field engineers',
        data: [],
      }
    }
  },

  async lockFieldEngineer(
    feUserId: number,
  ): Promise<ApiResponse<{ locked: boolean; expires_at?: string; seconds_remaining?: number }>> {
    try {
      const response = await apiClient.post(`/tickets/field-engineers/${feUserId}/lock`)
      return { success: true, data: response.data }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to lock field engineer',
      }
    }
  },

  async unlockFieldEngineer(feUserId: number): Promise<ApiResponse<{ released: boolean }>> {
    try {
      const response = await apiClient.delete(`/tickets/field-engineers/${feUserId}/lock`)
      return { success: true, data: response.data }
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message || error?.message || 'Failed to release field engineer lock',
      }
    }
  },

  async getFieldEngineerLockStatus(
    feUserId: number,
  ): Promise<
    ApiResponse<{
      is_locked: boolean
      locked_by_you: boolean
      lock_expires_at: string | null
      lock_seconds_remaining: number | null
    }>
  > {
    try {
      const response = await apiClient.get(`/tickets/field-engineers/${feUserId}/lock-status`)
      return { success: true, data: response.data }
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message || error?.message || 'Failed to fetch lock status',
      }
    }
  },

  async updateStoreDispatchLocation(
    storeId: number,
    payload: {
      latitude: number
      longitude: number
      location_address?: string | null
      normalized_address?: string | null
    },
  ): Promise<
    ApiResponse<{
      store_id: number
      latitude: number
      longitude: number
      address?: string | null
      normalized_address?: string | null
      geocoded_at?: string | null
    }>
  > {
    try {
      const response = await apiClient.patch(`/tickets/stores/${storeId}/location`, payload)
      const body = response.data as {
        data?: {
          store_id: number
          latitude: number
          longitude: number
          address?: string | null
          normalized_address?: string | null
          geocoded_at?: string | null
        }
      }
      return { success: true, data: body.data ?? (response.data as never) }
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message || error?.message || 'Failed to save store location pin',
      }
    }
  },

  async searchAssets(searchTerm: string, clientCode?: string, storeId?: number): Promise<ApiResponse<any[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const headers: Record<string, string> = {}
      
      // If clientCode is provided, set X-Client-Code header to search assets from that client's database
      if (clientCode) {
        headers['X-Client-Code'] = clientCode
      }

      const params: Record<string, any> = { search: searchTerm }
      
      // If storeId is provided, add it to params to filter assets by store
      if (storeId) {
        params.store_id = storeId
      }

      const response = await apiClient.get('/options/assets/search', {
        params,
        headers,
      })
      const data = response.data ?? []
      const assets = Array.isArray(data) ? data : []
      return { success: true, data: assets }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to search assets',
        data: [],
      }
    }
  },

  async searchStoreOptions(searchTerm: string, clientCode?: string, signal?: AbortSignal): Promise<ApiResponse<ClientOption[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const headers: Record<string, string> = {}
      
      // If clientCode is provided, set X-Client-Code header to search stores from that client's database
      if (clientCode) {
        headers['X-Client-Code'] = clientCode
      }

      const response = await apiClient.get('/options/stores/search', {
        params: {
          search: searchTerm,
        },
        headers,
        signal,
      })

      const storesData = response.data?.data || response.data || []
      
      // Transform API response to ClientOption format
      const transformedStores: ClientOption[] = storesData.map((store: any) => ({
        storeCode: store.branch_code || '',
        storeName: store.branch_name || '',
        ownershipType: store.business_unit_name || undefined,
        storeEmail: store.branch_email || undefined,
        storeContact: store.contact_number || store.mobile_number || undefined,
        alternateContact: store.mobile_number || undefined,
        amName: store.contact_person || undefined,
        amEmail: undefined, // Not available in StoreSearchResource
        storeHours: undefined, // Not available in StoreSearchResource
        // Additional fields for display
        branchAddress: store.branch_address || undefined,
        businessUnitName: store.business_unit_name || undefined,
        storeId: store.id || undefined,
        // Location fields
        locationAddress: store.location_address || undefined,
        normalizedAddress: store.normalized_address || undefined,
        latitude: store.latitude !== null && store.latitude !== undefined ? Number(store.latitude) : undefined,
        longitude: store.longitude !== null && store.longitude !== undefined ? Number(store.longitude) : undefined,
      }))

      return { success: true, data: transformedStores }
    } catch (error: any) {
      console.error('Error searching store options:', error)
      return { 
        success: false, 
        message: error?.response?.data?.message || error?.message || 'Failed to search stores',
        data: [] 
      }
    }
  },

  async searchToolPartOptions(searchTerm: string): Promise<ApiResponse<ToolPartSearchResponse>> {
    try {
      if (searchTerm.trim().length < 2) {
        return {
          success: true,
          data: { matches: [], suggestions: [], canCreate: false, normalizedQuery: '' },
        }
      }

      const response = await apiClient.get('/options/tool-parts/search', {
        params: { q: searchTerm },
      })

      const data = response.data ?? {}
      const matches = Array.isArray(data.matches) ? data.matches : []
      const suggestions = Array.isArray(data.suggestions) ? data.suggestions : []

      const normalizeOption = (item: Record<string, unknown>): ToolPartCatalogOption => ({
        id: Number(item.id),
        name: String(item.name ?? ''),
        matchType: item.matchType as ToolPartCatalogOption['matchType'],
        score: item.score != null ? Number(item.score) : undefined,
      })

      return {
        success: true,
        data: {
          matches: matches.map((item: Record<string, unknown>) => normalizeOption(item)),
          suggestions: suggestions.map((item: Record<string, unknown>) => normalizeOption(item)),
          canCreate: Boolean(data.canCreate),
          normalizedQuery: String(data.normalizedQuery ?? ''),
        },
      }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to search tool/part options',
        data: { matches: [], suggestions: [], canCreate: false, normalizedQuery: '' },
      }
    }
  },

  async upsertToolPart(name: string): Promise<ApiResponse<ToolPartCatalogOption>> {
    try {
      const response = await apiClient.post('/options/tool-parts', { name })
      const data = response.data ?? {}

      return {
        success: true,
        data: {
          id: Number(data.id),
          name: String(data.name ?? name),
          matchType: undefined,
          score: undefined,
        },
      }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to add tool/part',
      }
    }
  },

  /**
   * Coerce API value to engineer id (number | null | undefined). Handles {} or non-numeric from API.
   */
  _toEngineerId(v: unknown): number | null | undefined {
    if (v == null) return v as null | undefined
    if (typeof v === 'number' && Number.isFinite(v)) return v
    return undefined
  },

  /**
   * Map API parent ticket row (from list or show) to store shape.
   * Used by getParentTicket and by list page to sync DataTable data to store.
   * Includes extra fields for edit form: subject, agentId, scheduledAt, notes, workPermit, assets, source, engineers.
   */
  mapApiRowToParentTicket(
    row: Record<string, unknown>,
    fallbackId?: string
  ): import('@/types/parentTicket').ParentTicket & {
    ticket_number?: string
    store_id?: number
    store_code?: string
    subject?: string
    agentId?: string
    primaryEngineerId?: number | null
    additionalFeUserId?: number | null
    additionalFeUserIds?: number[]
    /** @deprecated Prefer additionalFeUserId */
    backupEngineerId?: number | null
    /** @deprecated Prefer additionalFeUserIds */
    backupEngineerIds?: number[]
    clientPurchaseOrderId?: number | null
    purchaseOrder?: import('@/types/ticket').ClientPurchaseOrder
    scheduledAt?: string
    notes?: string
    assets?: unknown[]
    attachments?: import('@/types/ticket').ParentTicketAttachment[]
    source?: string
    type?: string
    region_id?: number | null
    area_cluster_id?: number | null
    province_id?: number | null
    town_id?: number | null
    regionId?: number | null
    areaClusterId?: number | null
    provinceId?: number | null
    townId?: number | null
    incidentLocation?: import('@/types/ticket').IncidentLocation | null
    incident_location_type?: 'store' | 'location' | null
    incident_location_name?: string | null
    incident_location_address?: string | null
    incident_location_latitude?: number | null
    incident_location_longitude?: number | null
    incident_location_place_id?: string | number | null
    incident_location_source?: string | null
    incident_location_municipality?: string | null
    incident_location_province?: string | null
    incident_location_country?: string | null
    has_parts_needed?: boolean
    hasPartsNeeded?: boolean
  } {
    let assets: unknown[] | undefined
    const rawAssets = row.assets
    if (Array.isArray(rawAssets)) {
      assets = rawAssets
    } else if (typeof rawAssets === 'string') {
      try {
        const parsed = JSON.parse(rawAssets) as unknown
        assets = Array.isArray(parsed) ? parsed : undefined
      } catch {
        assets = undefined
      }
    }
    const scheduled = (row.scheduledAt ?? row.scheduled_at) as string | undefined
    const attachments = mapApiAttachments(row.attachments)
    const ticketNumber = (row.ticket_number ?? row.ticketId) as string | undefined
    const apiIncidentLocation = row.incidentLocation
    const incidentLocation =
      apiIncidentLocation && typeof apiIncidentLocation === 'object' && !Array.isArray(apiIncidentLocation)
        ? (apiIncidentLocation as import('@/types/ticket').IncidentLocation)
        : null
    const incidentLatitude =
      incidentLocation?.latitude != null
        ? Number(incidentLocation.latitude)
        : row.incidentLocationLatitude != null
          ? Number(row.incidentLocationLatitude)
          : row.incident_location_latitude != null
            ? Number(row.incident_location_latitude)
            : null
    const incidentLongitude =
      incidentLocation?.longitude != null
        ? Number(incidentLocation.longitude)
        : row.incidentLocationLongitude != null
          ? Number(row.incidentLocationLongitude)
          : row.incident_location_longitude != null
            ? Number(row.incident_location_longitude)
            : null
    const mappedIncidentLocation: import('@/types/ticket').IncidentLocation | null =
      incidentLocation || row.incidentLocationType || row.incident_location_type
        ? {
            type: (incidentLocation?.type ?? row.incidentLocationType ?? row.incident_location_type) as 'store' | 'location' | null,
            name: (incidentLocation?.name ?? row.incidentLocationName ?? row.incident_location_name) as string | null | undefined,
            address: (incidentLocation?.address ?? row.incidentLocationAddress ?? row.incident_location_address) as string | null | undefined,
            latitude: Number.isFinite(incidentLatitude) ? incidentLatitude : null,
            longitude: Number.isFinite(incidentLongitude) ? incidentLongitude : null,
            placeId: (incidentLocation?.placeId ?? row.incidentLocationPlaceId ?? row.incident_location_place_id) as string | number | null | undefined,
            source: (incidentLocation?.source ?? row.incidentLocationSource ?? row.incident_location_source) as string | null | undefined,
            municipality: (incidentLocation?.municipality ?? row.incidentLocationMunicipality ?? row.incident_location_municipality) as string | null | undefined,
            province: (incidentLocation?.province ?? row.incidentLocationProvince ?? row.incident_location_province) as string | null | undefined,
            country: (incidentLocation?.country ?? row.incidentLocationCountry ?? row.incident_location_country) as string | null | undefined,
            storeId: (incidentLocation?.storeId ?? row.store_id) as string | number | null | undefined,
            storeCode: (incidentLocation?.storeCode ?? row.store_code ?? row.storeCode) as string | null | undefined,
          }
        : null
    return {
      id: String(row.id ?? fallbackId ?? ''),
      ticket_number: ticketNumber ?? String(row.id ?? fallbackId ?? ''),
      ticketNumber: ticketNumber ?? String(row.id ?? fallbackId ?? ''),
      requestId: String(row.requestId ?? row.ticket_request_id ?? ''),
      clientName: (row.client as string) ?? (row.clientName as string) ?? '',
      clientOrg: (row.clientOrg as string) ?? '',
      agentName: (row.agent_name as string) ?? null,
      assignmentMethod:
        (row.assignmentMethod as string) ??
        (row.assignment_method as string) ??
        null,
      requestedAt: (row.submitted_at as string) ?? (row.created_at as string) ?? '',
      ageingLabel: (row.ageing as string) ?? '',
      holdTimeLabel: (row.hold_time as string) ?? (row.holdTime as string) ?? '',
      isAgePaused: Boolean(row.is_age_paused ?? row.isAgePaused),
      severity: ((row.severity ?? row.priority) as import('@/types/parentTicket').SeverityLabel) || '3',
      slaDueAt: (row.dueByAt as string) ?? (row.due_by_at as string) ?? null,
      status: ((row.status as string) ?? 'Open') as ParentTicketStatus,
      description: (row.issueDescription as string) ?? (row.description as string) ?? '',
      store_id: row.store_id as number | undefined,
      store_code: (row.store_code ?? row.storeCode) as string | undefined,
      incidentLocation: mappedIncidentLocation,
      incident_location_type: mappedIncidentLocation?.type ?? null,
      incident_location_name: mappedIncidentLocation?.name ?? null,
      incident_location_address: mappedIncidentLocation?.address ?? null,
      incident_location_latitude: mappedIncidentLocation?.latitude ?? null,
      incident_location_longitude: mappedIncidentLocation?.longitude ?? null,
      incident_location_place_id: mappedIncidentLocation?.placeId ?? null,
      incident_location_source: mappedIncidentLocation?.source ?? null,
      incident_location_municipality: mappedIncidentLocation?.municipality ?? null,
      incident_location_province: mappedIncidentLocation?.province ?? null,
      incident_location_country: mappedIncidentLocation?.country ?? null,
      subject: (row.subject as string) ?? undefined,
      agentId: row.agentId != null ? String(row.agentId) : undefined,
      primaryEngineerId: TicketService._toEngineerId(row.primary_engineer_id ?? row.primaryEngineerId),
      additionalFeUserId: TicketService._toEngineerId(
        row.additional_fe_user_id ?? row.additionalFeUserId ?? row.backup_engineer_id ?? row.backupEngineerId,
      ),
      additionalFeUserIds: (() => {
        const rawIds =
          row.additional_fe_user_ids ??
          row.additionalFeUserIds ??
          row.backup_engineer_ids ??
          row.backupEngineerIds
        return Array.isArray(rawIds)
          ? rawIds.map(Number).filter((id: number) => id > 0)
          : undefined
      })(),
      backupEngineerId: TicketService._toEngineerId(
        row.additional_fe_user_id ?? row.additionalFeUserId ?? row.backup_engineer_id ?? row.backupEngineerId,
      ),
      backupEngineerIds: (() => {
        const rawIds =
          row.additional_fe_user_ids ??
          row.additionalFeUserIds ??
          row.backup_engineer_ids ??
          row.backupEngineerIds
        return Array.isArray(rawIds)
          ? rawIds.map(Number).filter((id: number) => id > 0)
          : undefined
      })(),
      clientPurchaseOrderId:
        row.clientPurchaseOrderId != null
          ? Number(row.clientPurchaseOrderId)
          : row.client_purchase_order_id != null
            ? Number(row.client_purchase_order_id)
            : undefined,
      purchaseOrder: (row.purchaseOrder as import('@/types/ticket').ClientPurchaseOrder) ?? undefined,
      scheduledAt: scheduled ?? undefined,
      notes: (row.notes as string) ?? undefined,
      attachments,
      assets: assets ?? undefined,
      source: (row.source as string) ?? undefined,
      assignedTeamId:
        row.assignedTeamId != null
          ? Number(row.assignedTeamId)
          : row.assigned_team_id != null
            ? Number(row.assigned_team_id)
            : undefined,
      resolverTeamId:
        row.resolverTeamId != null
          ? Number(row.resolverTeamId)
          : row.resolver_team_id != null
            ? Number(row.resolver_team_id)
            : undefined,
      serviceTypeId:
        row.serviceTypeId != null
          ? Number(row.serviceTypeId)
          : row.service_type_id != null
            ? Number(row.service_type_id)
            : undefined,
      clientReferenceNumber:
        (row.clientReferenceNumber as string) ??
        (row.client_reference_number as string) ??
        undefined,
      billable: row.billable as boolean | null | undefined,
      poTrigger: Boolean(row.poTrigger ?? row.po_trigger ?? false),
      certificationRequired: Boolean(row.certificationRequired ?? row.certification_required ?? false),
      ticketSubmittedAt: (row.ticket_submitted_at ?? null) as string | null,
      dispatchPlan: row.dispatch_plan as import('@/types/parentTicket').ParentTicket['dispatchPlan'],
      dispatchRequired: row.dispatch_required as boolean | null | undefined,
      dispatchVersion: row.dispatch_version as string | undefined,
      certification_required: Boolean(row.certificationRequired ?? row.certification_required ?? false),
      brandId:
        row.brandId != null
          ? Number(row.brandId)
          : row.brand_id != null
            ? Number(row.brand_id)
            : null,
      brand_id:
        row.brandId != null
          ? Number(row.brandId)
          : row.brand_id != null
            ? Number(row.brand_id)
            : null,
      brandName: (row.brandName as string) ?? (row.brand_name as string) ?? null,
      brand_name: (row.brandName as string) ?? (row.brand_name as string) ?? null,
      issueCategoryId:
        row.issueCategoryId != null
          ? Number(row.issueCategoryId)
          : row.issue_category_id != null
            ? Number(row.issue_category_id)
            : undefined,
      issueSubCategoryId:
        row.issueSubCategoryId != null
          ? Number(row.issueSubCategoryId)
          : row.issue_sub_category_id != null
            ? Number(row.issue_sub_category_id)
            : undefined,
      issueItemId:
        row.issueItemId != null
          ? Number(row.issueItemId)
          : row.issue_item_id != null
            ? Number(row.issue_item_id)
            : undefined,
      region_id:
        row.region_id != null
          ? Number(row.region_id)
          : row.regionId != null
            ? Number(row.regionId)
            : undefined,
      area_cluster_id:
        row.area_cluster_id != null
          ? Number(row.area_cluster_id)
          : row.areaClusterId != null
            ? Number(row.areaClusterId)
            : undefined,
      province_id:
        row.province_id != null
          ? Number(row.province_id)
          : row.provinceId != null
            ? Number(row.provinceId)
            : undefined,
      town_id:
        row.town_id != null
          ? Number(row.town_id)
          : row.townId != null
            ? Number(row.townId)
            : undefined,
      regionId:
        row.regionId != null
          ? Number(row.regionId)
          : row.region_id != null
            ? Number(row.region_id)
            : undefined,
      areaClusterId:
        row.areaClusterId != null
          ? Number(row.areaClusterId)
          : row.area_cluster_id != null
            ? Number(row.area_cluster_id)
            : undefined,
      provinceId:
        row.provinceId != null
          ? Number(row.provinceId)
          : row.province_id != null
            ? Number(row.province_id)
            : undefined,
      townId:
        row.townId != null
          ? Number(row.townId)
          : row.town_id != null
            ? Number(row.town_id)
            : undefined,
      support_type: (row.support_type as import('@/types/parentTicket').SupportTypeCode) ?? undefined,
      supportType: (row.support_type as string) ?? undefined,
      has_parts_needed: Boolean(row.has_parts_needed ?? row.hasPartsNeeded ?? false),
      hasPartsNeeded: Boolean(row.hasPartsNeeded ?? row.has_parts_needed ?? false),
      total_ticket_time: (row.total_ticket_time as string) ?? undefined,
      feSchedules: (row.fe_schedules ?? row.feSchedules) as import('@/types/parentTicket').FESchedule[] | undefined,
      ticketParts: (row.ticket_parts ?? row.ticketParts) as import('@/types/parentTicket').TicketPart[] | undefined,
      csatScore:
        row.csatScore != null
          ? Number(row.csatScore)
          : row.csat_score != null
            ? Number(row.csat_score)
            : null,
      csatRespondedAt: (row.csatRespondedAt ?? row.csat_responded_at) as string | null | undefined,
      reopenedAt: (row.reopenedAt ?? row.reopened_at) as string | null | undefined,
    }
  },

  /**
   * Fetch a single parent ticket by id (GET /tickets/:id).
   * Use when editing a ticket that may not be in the list store (e.g. direct link or fresh load).
   */
  async getParentTicket(
    id: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<import('@/types/parentTicket').ParentTicket & { store_id?: number; store_code?: string; request?: import('@/types/ticket').RequestOption }>> {
    try {
      const { data } = await apiClient.get<{ data?: Record<string, unknown> }>(`/tickets/${id}`, {
        ...(signal ? { signal } : {}),
      })
      const row = data?.data ?? data
      if (!row || typeof row !== 'object') {
        return { success: false, message: 'Ticket not found' }
      }
      const mapped = TicketService.mapApiRowToParentTicket(row as Record<string, unknown>, id)
      const apiRequest = (row as Record<string, unknown>).request
      const request = apiRequest && typeof apiRequest === 'object' && !Array.isArray(apiRequest)
        ? mapApiRequestToRequestOption(apiRequest as Record<string, unknown>)
        : undefined
      const apiStore = (row as Record<string, unknown>).store
      const store = apiStore && typeof apiStore === 'object' && !Array.isArray(apiStore) && 'storeCode' in apiStore
        ? (apiStore as import('@/types/ticket').ClientOption)
        : undefined
      const apiAgent = (row as Record<string, unknown>).agent
      const agent = apiAgent && typeof apiAgent === 'object' && !Array.isArray(apiAgent) && 'id' in apiAgent
        ? (apiAgent as import('@/types/ticket').AgentOption)
        : undefined
      const apiPrimary = (row as Record<string, unknown>).primaryEngineer
      const primaryEngineer = apiPrimary && typeof apiPrimary === 'object' && !Array.isArray(apiPrimary) && 'id' in apiPrimary
        ? (apiPrimary as import('@/store/parentTicket').FieldEngineerSummary)
        : undefined
      const apiAdditional =
        (row as Record<string, unknown>).additionalEngineer ??
        (row as Record<string, unknown>).additionalFeUser ??
        (row as Record<string, unknown>).backupEngineer
      const additionalEngineer =
        apiAdditional && typeof apiAdditional === 'object' && !Array.isArray(apiAdditional) && 'id' in apiAdditional
          ? (apiAdditional as import('@/store/parentTicket').FieldEngineerSummary)
          : undefined
      const apiAdditionalEngineers =
        (row as Record<string, unknown>).additionalEngineers ??
        (row as Record<string, unknown>).backupEngineers
      const additionalEngineers = Array.isArray(apiAdditionalEngineers)
        ? (apiAdditionalEngineers as import('@/store/parentTicket').FieldEngineerSummary[])
        : undefined
      const apiAdditionalFeUserIds =
        (row as Record<string, unknown>).additionalFeUserIds ??
        (row as Record<string, unknown>).additional_fe_user_ids ??
        (row as Record<string, unknown>).backupEngineerIds ??
        (row as Record<string, unknown>).backup_engineer_ids
      const additionalFeUserIds = Array.isArray(apiAdditionalFeUserIds)
        ? (apiAdditionalFeUserIds as number[]).map(Number).filter((id) => id > 0)
        : undefined
      return {
        success: true,
        data: {
          ...mapped,
          ...(request && { request }),
          ...(store && { store }),
          ...(agent && { agent }),
          ...(primaryEngineer && { primaryEngineer }),
          ...(additionalEngineer && {
            additionalEngineer,
            backupEngineer: additionalEngineer,
          }),
          ...(additionalEngineers && {
            additionalEngineers,
            backupEngineers: additionalEngineers,
          }),
          ...(additionalFeUserIds && {
            additionalFeUserIds,
            backupEngineerIds: additionalFeUserIds,
          }),
        },
      }
    } catch (err: unknown) {
      if (axios.isCancel(err)) throw err
      const code = (err as { code?: string }).code
      if (code === 'ERR_CANCELED') throw err
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to load ticket'
      return { success: false, message }
    }
  },

  async createParentTicket(
    payload: ParentTicketFormValues,
    options?: { clientCode?: string },
  ): Promise<ApiResponse<{ ticketId: string; id?: number }>> {
    const body = buildParentTicketApiBody(payload, {
      requiresDispatch: payload.requiresDispatch,
    })

    try {
      const headers: Record<string, string> = {}
      if (options?.clientCode?.trim()) headers['X-Client-Code'] = options.clientCode.trim()
      const res = await apiClient.post<{ success: boolean; message?: string; data?: { ticketId: string; id?: number }; errors?: Record<string, string[]> }>(
        '/tickets',
        body,
        { headers },
      )
      const data = res.data
      if (data.success && data.data?.ticketId) {
        return {
          success: true,
          message: data.message ?? 'Ticket created',
          data: { ticketId: data.data.ticketId, id: data.data.id },
        }
      }
      if (data.errors) {
        return { success: false, message: data.message ?? 'Validation failed', errors: data.errors }
      }
      return {
        success: true,
        message: data.message ?? 'Ticket created',
        data: {
          ticketId: (data.data as { ticketId?: string })?.ticketId ?? '',
          id: (data.data as { id?: number })?.id,
        },
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; message?: string }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to create ticket'
      const errors = ax.response?.data?.errors
      return { success: false, message, ...(errors && { errors }) }
    }
  },

  async updateParentTicket(
    id: string,
    payload: ParentTicketFormValues,
    options?: { clearDispatchContext?: boolean; clientCode?: string },
  ): Promise<ApiResponse<unknown>> {
    const body = buildParentTicketApiBody(payload, {
      requiresDispatch: payload.requiresDispatch,
      clearDispatchContext: options?.clearDispatchContext,
    })

    try {
      const headers: Record<string, string> = {}
      if (options?.clientCode?.trim()) headers['X-Client-Code'] = options.clientCode.trim()
      const res = await apiClient.put<{ success: boolean; message?: string; data?: unknown; errors?: Record<string, string[]> }>(
        `/tickets/${id}`,
        body,
        { headers },
      )
      const data = res.data
      if (data.success) {
        useParentTicketStore.getState().updateTicket(id, {
          description: payload.issueDescription ?? undefined,
          severity: (payload.severity ?? payload.priority) as import('@/types/parentTicket').SeverityLabel,
        })
        return { success: true, message: data.message ?? 'Ticket updated' }
      }
      if (data.errors) return { success: false, message: data.message ?? 'Validation failed', errors: data.errors }
      return { success: true, message: data.message ?? 'Ticket updated' }
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          status?: number
          data?: {
            message?: string
            code?: string
            data?: { parts?: Array<{ id: number; part_name: string; child_ticket_id?: number | null }> }
            errors?: Record<string, string[]>
          }
        }
        message?: string
      }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to update ticket'
      const errors = ax.response?.data?.errors
      const code = ax.response?.data?.code
      const linkedParts = ax.response?.data?.data?.parts
      return {
        success: false,
        message,
        ...(errors && { errors }),
        ...(code && { code }),
        ...(linkedParts && { linkedParts }),
        ...(ax.response?.status && { status: ax.response.status }),
      }
    }
  },

  async uploadParentTicketAttachments(
    ticketId: string,
    options: {
      rows: ParentTicketAttachmentRow[]
      source: ParentTicketAttachmentSource
      replace?: boolean
      childTicketId?: number | null
    },
  ): Promise<ApiResponse<unknown>> {
    try {
      const formData = buildAttachmentUploadFormData(options)
      const res = await apiClient.post<{ success?: boolean; message?: string; data?: unknown }>(
        `/tickets/${ticketId}/attachments`,
        formData,
      )
      const data = res.data
      if (data?.success) {
        return { success: true, message: data.message ?? 'Attachments uploaded', data: data.data }
      }
      return { success: false, message: data?.message ?? 'Upload failed' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to upload attachments',
      }
    }
  },

  /**
   * Update parent ticket notes via PATCH /tickets/:id.
   * Delegates to patchParentTicket to avoid a near-duplicate PATCH helper.
   */
  async updateParentTicketNotes(ticketId: string, notes: string | null): Promise<ApiResponse<unknown>> {
    return TicketService.patchParentTicket(ticketId, { notes: notes ?? '' })
  },

  /** Partial update (e.g. clear primary/additional engineer on ticket view). */
  async patchParentTicket(
    ticketId: string,
    fields: Partial<{
      primaryEngineerId: number | null
      additionalFeUserId: number | null
      additionalFeUserIds: number[]
      /** @deprecated Prefer additionalFeUserId */
      backupEngineerId: number | null
      /** @deprecated Prefer additionalFeUserIds */
      backupEngineerIds: number[]
      status: string
      notes: string | null
    }>
  ): Promise<ApiResponse<unknown>> {
    try {
      const res = await apiClient.patch<{ success?: boolean; message?: string; data?: unknown }>(
        `/tickets/${ticketId}`,
        fields
      )
      const data = res.data
      if (data?.success) {
        return { success: true, message: data.message ?? 'Ticket updated', data: data.data }
      }
      return { success: false, message: data?.message ?? 'Update failed' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to update ticket' }
    }
  },

  // ──────────── Ticket Updates (comments / progress notes) ────────────

  async getTicketUpdates(
    ticketId: string,
    page = 1,
    perPage = 20
  ): Promise<ApiResponse<{ data: TicketUpdate[]; meta?: Record<string, unknown> }>> {
    try {
      const res = await apiClient.get(`/tickets/${ticketId}/updates`, {
        params: { page, per_page: perPage },
      })
      return { success: true, data: res.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load updates' }
    }
  },

  async createTicketUpdate(
    ticketId: string,
    payload: TicketUpdateFormValues
  ): Promise<ApiResponse<TicketUpdate>> {
    try {
      const res = await apiClient.post<{ success: boolean; message?: string; data?: TicketUpdate }>(
        `/tickets/${ticketId}/updates`,
        payload
      )
      if (res.data.success) {
        return { success: true, message: res.data.message ?? 'Update added', data: res.data.data }
      }
      return { success: false, message: res.data.message ?? 'Failed to add update' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to add update' }
    }
  },

  async editTicketUpdate(
    ticketId: string,
    updateId: number,
    payload: TicketUpdateFormValues
  ): Promise<ApiResponse<TicketUpdate>> {
    try {
      const res = await apiClient.put<{ success: boolean; message?: string; data?: TicketUpdate }>(
        `/tickets/${ticketId}/updates/${updateId}`,
        payload
      )
      if (res.data.success) {
        return { success: true, message: res.data.message ?? 'Update modified', data: res.data.data }
      }
      return { success: false, message: res.data.message ?? 'Failed to edit update' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to edit update' }
    }
  },

  async deleteTicketUpdate(
    ticketId: string,
    updateId: number
  ): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/tickets/${ticketId}/updates/${updateId}`)
      return { success: true, message: 'Update removed' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to remove update' }
    }
  },

  async getFESchedules(ticketId: string, date?: string): Promise<ApiResponse<FESchedule[]>> {
    try {
      const params = date ? { date } : {}
      const res = await apiClient.get<{ success: boolean; data?: FESchedule[] }>(`/tickets/${ticketId}/fe-schedules`, { params })
      return { success: true, data: res.data?.data ?? [] }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load FE schedules' }
    }
  },

  /** Get all schedules for a given FE on a date (cross-ticket). Used by Recommended FE modal daily timeline. */
  async getFEAvailability(
    ticketId: string,
    feUserId: number,
    date: string
  ): Promise<ApiResponse<FESchedule[]>> {
    try {
      const res = await apiClient.get<{ success: boolean; data?: FESchedule[] }>(
        `/tickets/${ticketId}/fe-availability`,
        { params: { fe_user_id: feUserId, date } }
      )
      return { success: true, data: res.data?.data ?? [] }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load FE availability' }
    }
  },

  /** Cross-ticket FE availability without a ticket context (deferred parent ticket form). */
  async getFEAvailabilityGlobal(
    storeId: number,
    feUserIds: number[],
    date: string,
    clientCode?: string,
    currentTicketId?: string
  ): Promise<ApiResponse<Record<number, FESchedule[]>>> {
    try {
      if (feUserIds.length === 0) {
        return { success: true, data: {} }
      }
      const headers: Record<string, string> = {}
      if (clientCode) headers['X-Client-Code'] = clientCode
      const params: Record<string, string | number | number[]> = {
        date,
        fe_user_ids: feUserIds,
      }
      if (currentTicketId) {
        params.current_ticket_id = Number(currentTicketId)
      }
      const res = await apiClient.get<{ success: boolean; data?: Record<string, FESchedule[]> }>(
        `/organization/field-engineers/recommend/${storeId}/availability`,
        { params, headers }
      )
      const raw = res.data?.data ?? {}
      const byUser: Record<number, FESchedule[]> = {}
      for (const [key, schedules] of Object.entries(raw)) {
        const userId = Number(key)
        if (Number.isFinite(userId) && userId > 0) {
          byUser[userId] = schedules ?? []
        }
      }
      return { success: true, data: byUser }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load FE availability' }
    }
  },

  async getFEAvailabilityGlobalForLocation(
    feUserIds: number[],
    date: string,
    clientCode?: string,
    currentTicketId?: string
  ): Promise<ApiResponse<Record<number, FESchedule[]>>> {
    try {
      if (feUserIds.length === 0) {
        return { success: true, data: {} }
      }
      const headers: Record<string, string> = {}
      if (clientCode) headers['X-Client-Code'] = clientCode
      const params: Record<string, string | number | number[]> = {
        date,
        fe_user_ids: feUserIds,
      }
      if (currentTicketId) {
        params.current_ticket_id = Number(currentTicketId)
      }
      const res = await apiClient.get<{ success: boolean; data?: Record<string, FESchedule[]> }>(
        '/organization/field-engineers/recommend/location/availability',
        { params, headers }
      )
      const raw = res.data?.data ?? {}
      const byUser: Record<number, FESchedule[]> = {}
      for (const [key, schedules] of Object.entries(raw)) {
        const userId = Number(key)
        if (Number.isFinite(userId) && userId > 0) {
          byUser[userId] = schedules ?? []
        }
      }
      return { success: true, data: byUser }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load FE availability' }
    }
  },

  async createFESchedule(
    ticketId: string,
    payload: {
      fe_user_id: number
      schedule_date: string
      schedule_end_date?: string | null
      schedule_time?: string | null
      schedule_end_time?: string | null
      role: 'primary' | 'additional' | 'secondary'
      latitude?: number | null
      longitude?: number | null
      location_address?: string | null
      contact_number_override?: string | null
      child_ticket_id?: number | null
      create_fe_visit_task?: boolean
    }
  ): Promise<ApiResponse<FESchedule>> {
    try {
      const res = await apiClient.post<{ success: boolean; data?: FESchedule }>(`/tickets/${ticketId}/fe-schedules`, payload)
      return { success: true, data: res.data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to add FE schedule', status: ax.response?.status }
    }
  },

  async updateFESchedule(
    ticketId: string,
    id: number,
    payload: {
      fe_user_id?: number
      schedule_date?: string
      schedule_end_date?: string | null
      schedule_time?: string | null
      schedule_end_time?: string | null
      role?: 'primary' | 'additional' | 'secondary'
      latitude?: number | null
      longitude?: number | null
      location_address?: string | null
      contact_number_override?: string | null
      child_ticket_id?: number | null
      create_fe_visit_task?: boolean
    }
  ): Promise<ApiResponse<FESchedule>> {
    try {
      const res = await apiClient.put<{
        success?: boolean
        status?: string | boolean
        message?: string
        data?: FESchedule
      }>(`/tickets/${ticketId}/fe-schedules/${id}`, payload)
      const data = res.data
      if (data && (data.success === false || data.status === 'error' || data.status === false)) {
        return {
          success: false,
          message: data.message ?? 'Failed to update FE schedule',
          status: res.status,
        }
      }
      return { success: true, data: data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to update FE schedule', status: ax.response?.status }
    }
  },

  async deleteFESchedule(ticketId: string, id: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/tickets/${ticketId}/fe-schedules/${id}`)
      return { success: true }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to remove FE schedule', status: ax.response?.status }
    }
  },

  async withdrawFESchedule(
    ticketId: string,
    scheduleId: number,
    payload?: { reason?: string },
  ): Promise<ApiResponse<FESchedule>> {
    try {
      const res = await apiClient.post<{ success?: boolean; data?: FESchedule; message?: string }>(
        `/tickets/${ticketId}/fe-schedules/${scheduleId}/withdraw`,
        payload ?? {},
      )
      return { success: true, data: res.data?.data, message: res.data?.message }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to withdraw FE dispatch',
        status: ax.response?.status,
      }
    }
  },

  async withdrawFeSchedulesBatch(
    ticketId: string,
    payload: { schedule_ids: number[]; reason?: string },
  ): Promise<ApiResponse<{ withdrawn: number; schedule_ids: number[] }>> {
    try {
      const res = await apiClient.post<{
        success?: boolean
        data?: { withdrawn: number; schedule_ids: number[] }
        message?: string
      }>(`/tickets/${ticketId}/fe-schedules/withdraw-batch`, payload)
      return { success: true, data: res.data?.data, message: res.data?.message }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to withdraw FE schedules',
        status: ax.response?.status,
      }
    }
  },

  /** Create FE Visit child tasks for parent schedules that have no child_ticket_id. */
  async createMissingFeVisits(ticketId: string): Promise<ApiResponse<{ created?: number; schedules?: FESchedule[] }>> {
    try {
      const res = await apiClient.post<{
        success?: boolean
        message?: string
        data?: { created?: number; schedules?: FESchedule[] } | FESchedule[]
      }>(`/tickets/${ticketId}/fe-schedules/create-missing-fe-visits`)
      const data = res.data?.data
      return {
        success: true,
        message: res.data?.message,
        data: Array.isArray(data) ? { schedules: data, created: data.length } : data,
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to create missing FE Visit tasks',
        status: ax.response?.status,
      }
    }
  },

  async getTicketParts(ticketId: string): Promise<
    ApiResponse<TicketPart[]> & { openProcurementTask?: import('@/types/parentTicket').OpenProcurementTask | null }
  > {
    try {
      const res = await apiClient.get<{
        success: boolean
        data?: TicketPart[]
        open_procurement_task?: import('@/types/parentTicket').OpenProcurementTask | null
      }>(`/tickets/${ticketId}/parts`)
      return {
        success: true,
        data: res.data?.data ?? [],
        openProcurementTask: res.data?.open_procurement_task ?? null,
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load parts' }
    }
  },

  async createTicketPart(
    ticketId: string,
    payload: Partial<Omit<TicketPart, 'id' | 'ticket_id' | 'created_at' | 'updated_at'>>
  ): Promise<ApiResponse<TicketPart>> {
    try {
      const res = await apiClient.post<{ success: boolean; data?: TicketPart }>(`/tickets/${ticketId}/parts`, payload)
      return { success: true, data: res.data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to add part' }
    }
  },

  async updateTicketPart(
    ticketId: string,
    id: number,
    payload: Partial<Omit<TicketPart, 'id' | 'ticket_id' | 'created_at' | 'updated_at'>>
  ): Promise<ApiResponse<TicketPart>> {
    try {
      const res = await apiClient.put<{ success: boolean; data?: TicketPart }>(`/tickets/${ticketId}/parts/${id}`, payload)
      return { success: true, data: res.data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to update part' }
    }
  },

  async deleteTicketPart(ticketId: string, id: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/tickets/${ticketId}/parts/${id}`)
      return { success: true }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to remove part' }
    }
  },

  async convertPartToTask(
    ticketId: string,
    partId: number,
  ): Promise<ApiResponse<{ child_ticket_id: number; childTicketId: number; task_number?: string }>> {
    try {
      const res = await apiClient.post<{
        success: boolean
        data?: { child_ticket_id: number; childTicketId: number; task_number?: string }
      }>(`/tickets/${ticketId}/parts/${partId}/convert-to-task`)
      return { success: true, data: res.data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to create procurement task' }
    }
  },

  async bulkConvertPartsToTask(
    ticketId: string,
    partIds: number[],
    existingChildTicketId?: number | null,
  ): Promise<ApiResponse<import('@/types/parentTicket').BulkConvertPartsResponse>> {
    try {
      const res = await apiClient.post<{
        success: boolean
        data?: import('@/types/parentTicket').BulkConvertPartsResponse
      }>(`/tickets/${ticketId}/parts/bulk-convert-to-task`, {
        part_ids: partIds,
        ...(existingChildTicketId != null ? { existing_child_ticket_id: existingChildTicketId } : {}),
      })
      return { success: true, data: res.data?.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to send parts to procurement' }
    }
  },
}
